/**
 * SUNTRONIX ONE — Login brand-story panel.
 *
 * Rotating narrative that answers "Who is Suntronix?" alongside the
 * authentication panel.  Auto-advances every ~6s and pauses on hover
 * / manual navigation.  Respects prefers-reduced-motion.
 */
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Building2, Package, RefreshCcw, ShieldCheck, Cpu, Recycle,
  Sparkles, ChevronLeft, ChevronRight, Pause, Play,
} from "lucide-react";

const PANELS = [
  {
    key: "who",
    step: "01",
    title: "Who we are",
    heading: "Pioneering enterprise technology since 1999.",
    body: "Suntronix has evolved alongside workplace technology — from personal devices and emerging mobile technology to enterprise deployment, lifecycle services and global technology operations.",
    icon: Building2,
    accent: "#2962FF",
  },
  {
    key: "manage",
    step: "02",
    title: "What we manage",
    heading: "Technology operations — orchestrated.",
    body: "Procurement · Deployment · Management · Support · Recovery · Reuse · ITAD & responsible recycling — one operating system across the complete lifecycle.",
    icon: Package,
    accent: "#00C8FF",
  },
  {
    key: "lifecycle",
    step: "03",
    title: "The technology lifecycle",
    heading: "Every asset. One history.",
    body: "Golden Asset Record captures serial, custody, employee, location and lifecycle stage in one authoritative timeline — no reconstruction, no reconciliation gaps.",
    icon: RefreshCcw,
    accent: "#18D5A4",
  },
  {
    key: "one",
    step: "04",
    title: "SUNTRONIX ONE",
    heading: "Your technology estate. Managed as one.",
    body: "One connected platform for customers, employees, technology, logistics, support, recovery, commercial workflows and lifecycle intelligence.",
    icon: Sparkles,
    accent: "#7657FF",
  },
  {
    key: "ops",
    step: "05",
    title: "Connected operations",
    heading: "Integrations, events and intelligence.",
    body: "ONE CONNECT wires Microsoft Intune, Apple Business, Google Workspace, HRIS, courier and OEM systems — every device becomes evidence on the Golden Asset Record, never overwrites it.",
    icon: Cpu,
    accent: "#FFB84D",
  },
  {
    key: "reuse",
    step: "06",
    title: "Reuse & responsible lifecycle",
    heading: "Reuse Before Buy. Recycle with certainty.",
    body: "Inventory matching, Device Fit, repair and redeployment, secure data sanitisation, testing, grading, buyback, remarketing and certified recycling — all with chain-of-custody evidence.",
    icon: Recycle,
    accent: "#18D5A4",
  },
  {
    key: "workspace",
    step: "07",
    title: "Your organisation. One workspace.",
    heading: "Secure, authorised access only.",
    body: "Tenant-isolated workspace — customer, subsidiary, legal entity, country, location, employee and asset hierarchy enforced end-to-end.",
    icon: ShieldCheck,
    accent: "#2962FF",
  },
];

const ROTATE_MS = 6000;

export default function LoginStoryPanel({ index, onIndexChange }) {
  const location = useLocation();
  const controlled = typeof index === "number" && typeof onIndexChange === "function";
  const [innerI, setInnerI] = useState(0);
  const i = controlled ? index : innerI;
  const setI = controlled ? onIndexChange : setInnerI;
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Deep-link support — ?panel=lifecycle / who / manage / one / ops /
  // reuse / workspace opens the story on the requested panel and pauses
  // auto-rotation so the sender can share a stable view without exposing
  // any customer data.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = (params.get("panel") || "").toLowerCase().trim();
    if (!key) return;
    const idx = PANELS.findIndex((p) => p.key === key);
    if (idx >= 0) { setI(idx); setPaused(true); }
  }, [location.search, setI]);

  useEffect(() => {
    if (paused) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timerRef.current = setInterval(
      () => setI((k) => ((typeof k === "number" ? k : i) + 1) % PANELS.length),
      ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, controlled, setI, i]);

  const go = (dir) => {
    const next = (i + dir + PANELS.length) % PANELS.length;
    setI(next); setPaused(true);
  };
  const goTo = (idx) => { setI(idx); setPaused(true); };
  const P = PANELS[i];
  const Icon = P.icon;

  return (
    <div className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="login-story-panel">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#C6D2EA]/70 font-mono">
          {P.step} / 07
        </span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] uppercase tracking-[0.16em] text-white/80">
          {P.title}
        </span>
      </div>

      <div key={P.key} className="relative"
              style={{ animation: "story-in 400ms ease-out" }}>
        <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl mb-5"
                style={{ background: `${P.accent}1F`, boxShadow: `0 0 24px ${P.accent}55` }}>
          <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: P.accent }} />
        </div>
        <h2 className="font-heading text-[44px] xl:text-[56px] leading-[1.02] tracking-[-0.028em] text-white max-w-[620px]">
          {P.heading}
        </h2>
        <p className="mt-6 text-[16px] leading-[1.6] text-[#C6D2EA] max-w-[560px]">
          {P.body}
        </p>
      </div>

      {/* Controls */}
      <div className="mt-8 flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          {PANELS.map((p, idx) => (
            <button key={p.key} onClick={() => goTo(idx)}
                      aria-label={`Panel ${idx + 1}: ${p.title}`}
                      data-testid={`login-story-dot-${idx}`}
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                          idx === i ? "w-8 bg-white" : "w-1.5 bg-white/25 hover:bg-white/50"}`} />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => go(-1)} aria-label="Previous panel"
                    data-testid="login-story-prev"
                    className="h-7 w-7 rounded-full border border-white/15 hover:bg-white/5 text-[#C6D2EA] hover:text-white grid place-items-center transition-colors">
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.6} />
          </button>
          <button onClick={() => setPaused((p) => !p)} aria-label={paused ? "Play" : "Pause"}
                    data-testid="login-story-toggle"
                    className="h-7 w-7 rounded-full border border-white/15 hover:bg-white/5 text-[#C6D2EA] hover:text-white grid place-items-center transition-colors">
            {paused ? <Play className="h-3 w-3" strokeWidth={1.6} />
                        : <Pause className="h-3 w-3" strokeWidth={1.6} />}
          </button>
          <button onClick={() => go(1)} aria-label="Next panel"
                    data-testid="login-story-next"
                    className="h-7 w-7 rounded-full border border-white/15 hover:bg-white/5 text-[#C6D2EA] hover:text-white grid place-items-center transition-colors">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes story-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}
